'use client';

import { useState } from 'react';
import { createBrowserClient } from '@supabase/ssr';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ShieldCheck, Loader2, ArrowLeft } from 'lucide-react';

interface Props {
  email: string;
  onBack: () => void;
  enforce: boolean;
}

/**
 * SSO login form — shown when a user's email domain has SSO configured.
 * Calls supabase.auth.signInWithSSO({ domain }) to initiate the SAML flow.
 */
export function SsoLoginForm({ email, onBack, enforce }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const domain = email.split('@')[1] ?? '';

  async function handleSsoLogin() {
    setLoading(true);
    setError(null);

    try {
      const supabase = createBrowserClient(
        process.env.NEXT_PUBLIC_SUPABASE_URL!,
        process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
      );

      const baseUrl =
        typeof window !== 'undefined'
          ? window.location.origin
          : '';

      const { data, error: ssoError } = await supabase.auth.signInWithSSO({
        domain,
        options: {
          redirectTo: `${baseUrl}/api/auth/sso/callback`,
        },
      });

      if (ssoError) {
        setError(ssoError.message);
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setError('Failed to initiate SSO login');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <ShieldCheck className="h-5 w-5 text-blue-600 shrink-0" />
        <div>
          <p className="text-sm font-medium text-blue-900">
            Single Sign-On is {enforce ? 'required' : 'available'} for {domain}
          </p>
          <p className="text-xs text-blue-700 mt-0.5">
            {enforce
              ? 'Your organization requires SSO authentication.'
              : 'Your organization has SSO configured. You can sign in with your company account.'}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Email</Label>
        <Input value={email} disabled className="bg-muted" />
      </div>

      {error && (
        <div className="text-sm p-3 rounded-lg bg-red-50 text-red-700">
          {error}
        </div>
      )}

      <Button
        className="w-full"
        onClick={handleSsoLogin}
        disabled={loading}
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Redirecting to SSO...
          </>
        ) : (
          <>
            <ShieldCheck className="h-4 w-4 mr-2" />
            Sign in with SSO
          </>
        )}
      </Button>

      {!enforce && (
        <Button
          variant="ghost"
          className="w-full"
          onClick={onBack}
          disabled={loading}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Use password instead
        </Button>
      )}
    </div>
  );
}
